import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, BarChart3, ArrowUpRight, CreditCard, Building2, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Cell } from 'recharts';
import { Badge } from '../../components/ui/badge';
import api from '../../api';

const PLAN_COLORS = ['#516EFA', '#14C8C4', '#F59E0B', '#8B5CF6', '#EF4444'];
const fmt = (n) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n || 0);

export default function RevenuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/master/revenue').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="p-8 space-y-5">{[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}</div>
  );

  const mrr = data?.mrr || 0;
  const arr = data?.arr || 0;
  const activeSubs = data?.active_subscriptions || 0;
  const revByPlan = data?.revenue_by_plan || [];
  const monthlyTrend = data?.monthly_trend || [];
  const history = data?.payment_history || [];

  return (
    <div className="p-6 lg:p-8 space-y-7 animate-fade-in" data-testid="revenue-page">
      {/* Header */}
      <div>
        <h1 className="font-outfit text-2xl font-bold text-slate-900">Revenue & Billing</h1>
        <p className="text-slate-500 text-sm mt-0.5">Platform-wide subscription revenue analytics</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Monthly Recurring Revenue', value: fmt(mrr), icon: DollarSign, color: '#516EFA', bg: '#eef1ff' },
          { label: 'Annual Recurring Revenue', value: fmt(arr), icon: TrendingUp, color: '#14C8C4', bg: '#e0fafa' },
          { label: 'Active Subscriptions', value: activeSubs, icon: CreditCard, color: '#8B5CF6', bg: '#f3f0ff' },
          { label: 'Avg Revenue / School', value: activeSubs > 0 ? fmt(mrr / activeSubs) : '₦0', icon: Building2, color: '#F59E0B', bg: '#fffbeb' },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: k.bg }}>
                <k.icon className="w-4 h-4" style={{ color: k.color }} />
              </div>
            </div>
            <p className="font-outfit text-xl font-bold text-slate-900">{typeof k.value === 'number' ? k.value.toLocaleString() : k.value}</p>
            <p className="text-xs text-slate-500 mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue by Plan */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h3 className="font-outfit font-semibold text-slate-900 mb-5">MRR by Plan</h3>
          {revByPlan.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revByPlan} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="plan" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v > 999 ? `₦${(v/1000).toFixed(0)}k` : `₦${v}`} />
                <Tooltip formatter={(v) => [fmt(v), 'MRR']} contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Bar dataKey="mrr" radius={[6, 6, 0, 0]}>
                  {revByPlan.map((_, i) => <Cell key={i} fill={PLAN_COLORS[i % PLAN_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-slate-300 text-sm">No revenue data yet</div>
          )}
        </div>

        {/* Monthly Revenue Trend */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h3 className="font-outfit font-semibold text-slate-900 mb-5">Monthly Revenue Trend</h3>
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthlyTrend}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14C8C4" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#14C8C4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v > 999 ? `₦${(v/1000).toFixed(0)}k` : `₦${v}`} />
                <Tooltip formatter={(v) => [fmt(v), 'Revenue']} contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="#14C8C4" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-slate-300 text-sm">No trend data yet</div>
          )}
        </div>
      </div>

      {/* Plan Breakdown Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-outfit font-semibold text-slate-900">Revenue Breakdown by Plan</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="edu-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Schools</th>
                <th>Monthly Price</th>
                <th>MRR</th>
                <th>ARR</th>
              </tr>
            </thead>
            <tbody>
              {revByPlan.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">No plans configured yet</td></tr>
              ) : revByPlan.map((r, i) => (
                <tr key={i}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: PLAN_COLORS[i % PLAN_COLORS.length] }} />
                      <span className="font-medium text-slate-800">{r.plan}</span>
                    </div>
                  </td>
                  <td>{r.schools}</td>
                  <td>{fmt(r.monthly_price)}</td>
                  <td className="font-semibold text-slate-900">{fmt(r.mrr)}</td>
                  <td className="font-semibold text-emerald-700">{fmt(r.arr)}</td>
                </tr>
              ))}
              {revByPlan.length > 0 && (
                <tr className="bg-slate-50 font-bold">
                  <td>Total</td>
                  <td>{revByPlan.reduce((s, r) => s + r.schools, 0)}</td>
                  <td>—</td>
                  <td className="text-[#516EFA]">{fmt(mrr)}</td>
                  <td className="text-emerald-700">{fmt(arr)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-outfit font-semibold text-slate-900">Payment History</h3>
        </div>
        {history.length === 0 ? (
          <div className="py-12 text-center">
            <CreditCard className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-400">No payment records yet</p>
            <p className="text-xs text-slate-300 mt-1">Payments will appear here once schools start paying subscriptions</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="edu-table">
              <thead><tr><th>School</th><th>Plan</th><th>Amount</th><th>Date</th><th>Status</th></tr></thead>
              <tbody>
                {history.map((p, i) => (
                  <tr key={i}>
                    <td className="font-medium">{p.school_name || '—'}</td>
                    <td>{p.plan_name || '—'}</td>
                    <td className="font-semibold">{fmt(p.amount)}</td>
                    <td className="text-slate-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-NG') : '—'}</td>
                    <td><Badge className={p.status === 'success' ? 'badge-success' : 'badge-neutral'}>{p.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
